import { Fragment, useCallback, useEffect, useState } from 'react';
import { intervalToDuration, formatDuration } from 'date-fns';
import { ChevronDown, RefreshCw, ScrollText, Trash } from 'lucide-react';
import { buildsApi } from '@/lib/api/builds';
import type { BuildHistory } from '@/types/entities';
import { BuildStatusBadge } from '@/components/custom/build-status-badge';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type BuildHistoryPanelProps = {
  tenantSlug: string;
  title?: string;
  description?: string;
};

export function BuildHistoryPanel({
  tenantSlug,
  title = 'Build History',
  description = 'View the complete build history.',
}: BuildHistoryPanelProps) {
  const [builds, setBuilds] = useState<BuildHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBuildId, setExpandedBuildId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [buildToDelete, setBuildToDelete] = useState<BuildHistory | undefined>();

  const fetchBuilds = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await buildsApi.getHistory(tenantSlug, 100);
      setBuilds(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load build history');
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    fetchBuilds();
  }, [fetchBuilds]);

  const handleDelete = (build: BuildHistory) => {
    setBuildToDelete(build);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!buildToDelete) return;

    try {
      await buildsApi.delete(tenantSlug, buildToDelete.id);
      setDeleteDialogOpen(false);
      setBuildToDelete(undefined);
      await fetchBuilds();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete build');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading build history...</div>
      </div>
    );
  }

  if (error) {
    return <div className="bg-destructive/10 text-destructive p-4 rounded-md">{error}</div>;
  }

  if (builds.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <ScrollText className="size-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No build history found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchBuilds}>
              <RefreshCw className="size-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead className="w-24">Duration</TableHead>
                  <TableHead className="w-24">Exit Code</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {builds.map((build) => (
                  <Fragment key={build.id}>
                    <TableRow>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          #{build.id}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <BuildStatusBadge status={build.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(build.started_at)}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(build.completed_at)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {getDuration(build.started_at, build.completed_at) || '-'}
                      </TableCell>
                      <TableCell>
                        {build.exit_code === null ? (
                          <span className="text-muted-foreground">-</span>
                        ) : (
                          <Badge variant={build.exit_code === 0 ? 'default' : 'destructive'}>
                            {build.exit_code}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() =>
                              setExpandedBuildId(expandedBuildId === build.id ? null : build.id)
                            }
                            title="Toggle logs"
                          >
                            <ChevronDown
                              className={`size-4 transition-transform ${
                                expandedBuildId === build.id ? 'rotate-180' : ''
                              }`}
                            />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(build)}
                            title="Delete build"
                          >
                            <Trash className="size-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedBuildId === build.id && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/20">
                          <div className="space-y-4 py-2">
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                              <div>
                                <p className="text-xs text-muted-foreground">Build ID</p>
                                <p className="text-sm font-mono">#{build.id}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Status</p>
                                <BuildStatusBadge status={build.status} />
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Started</p>
                                <p className="text-sm">{formatDate(build.started_at)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Completed</p>
                                <p className="text-sm">{formatDate(build.completed_at)}</p>
                              </div>
                            </div>

                            {(build.stdout || build.stderr) && (
                              <div className="space-y-3">
                                {build.stdout && (
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-2 font-medium">
                                      Standard Output
                                    </p>
                                    <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto max-h-96 overflow-y-auto font-mono">
                                      {build.stdout}
                                    </pre>
                                  </div>
                                )}
                                {build.stderr && (
                                  <div>
                                    <p className="text-xs text-destructive mb-2 font-medium">
                                      Standard Error
                                    </p>
                                    <pre className="bg-destructive/10 p-3 rounded-md text-xs overflow-x-auto max-h-96 overflow-y-auto font-mono text-destructive">
                                      {build.stderr}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Build"
        description={`Are you sure you want to delete build #${buildToDelete?.id}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="OK"
      />
    </div>
  );
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const getDuration = (started: string, completed: string | null) => {
  if (!completed) return null;

  const startDate = new Date(started);
  const endDate = new Date(completed);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;

  const duration = intervalToDuration({ start: startDate, end: endDate });
  if ((duration.hours ?? 0) > 0) {
    return formatDuration(duration, { format: ['hours', 'minutes', 'seconds'] });
  }
  if ((duration.minutes ?? 0) > 0) {
    return formatDuration(duration, { format: ['minutes', 'seconds'] });
  }
  return formatDuration(duration, { format: ['seconds'] });
};
